import { useEffect } from "react";

const SITE_NAME = "EntranceZone AI";
const DEFAULT_DESC =
  "EntranceZone AI — AI-powered career counselling, adaptive career assessment, college & rank predictors, scholarships, and expert mentorship for students and parents.";
const OG_IMAGE = "/og-image.jpg";

const origin = () =>
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_SITE_URL ||
  "https://ai.entrancezone.com";

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Client-side SEO helper. Sets title, description, canonical, robots,
 * Open Graph, Twitter Card and optional JSON-LD structured data per route.
 */
export default function Seo({
  title,
  description = DEFAULT_DESC,
  path = "",
  image = OG_IMAGE,
  noindex = false,
  jsonLd = null,
}) {
  useEffect(() => {
    const base = origin();
    const url = base + (path || (typeof window !== "undefined" ? window.location.pathname : ""));
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Architecting Human Potential`;
    const imageUrl = image?.startsWith("http") ? image : base + image;

    document.title = fullTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", url);

    // Open Graph
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", imageUrl);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", imageUrl);

    // JSON-LD structured data (per page)
    const id = "seo-jsonld-page";
    let script = document.getElementById(id);
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, image, noindex, jsonLd]);

  return null;
}
